import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { api, type MintedToken, type TokenSummary } from '../api';

const API_URL = 'https://mle2zqvap3.execute-api.eu-west-2.amazonaws.com/v1';
const PACKAGE = '@dwlf/tickets-mcp';

function Copyable({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(text).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => undefined,
    );
  };
  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: 'action.hover',
        borderRadius: 1,
        p: 1.5,
        pr: 5,
        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
        fontSize: 12.5,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
      }}
    >
      {text}
      <IconButton
        size="small"
        onClick={copy}
        aria-label={`Copy ${label}`}
        sx={{ position: 'absolute', top: 4, right: 4 }}
      >
        <ContentCopyIcon fontSize="inherit" color={copied ? 'success' : 'inherit'} />
      </IconButton>
    </Box>
  );
}

function NewTokenReveal({ token, onDone }: { token: MintedToken; onDone: () => void }) {
  const addCommand =
    `claude mcp add dwlf-tickets --scope user \\\n` +
    `  -e DWLF_TICKETS_API=${API_URL} \\\n` +
    `  -e DWLF_TICKETS_TOKEN=${token.plaintext} \\\n` +
    `  -- npx -y ${PACKAGE}`;

  return (
    <Alert severity="success" icon={false} sx={{ mb: 2 }}>
      <AlertTitle>Token created — copy it now</AlertTitle>
      <Typography variant="body2" sx={{ mb: 1 }}>
        This is the only time it is shown. It is stored as a hash and can never be retrieved
        again — if you lose it, revoke it and make a new one.
      </Typography>
      <Copyable text={token.plaintext} label="token" />

      <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
        <strong>To connect Claude Code</strong>, run this once in a terminal, then restart Claude:
      </Typography>
      <Copyable text={addCommand} label="setup command" />

      <Box sx={{ mt: 2 }}>
        <Button size="small" variant="outlined" onClick={onDone}>
          Done
        </Button>
      </Box>
    </Alert>
  );
}

const SCOPES = [
  { id: 'tickets:read', label: 'Read tickets' },
  { id: 'tickets:write', label: 'Create & edit tickets' },
  { id: 'comments:write', label: 'Comment' },
];

function CreateForm({ onCreated }: { onCreated: (token: MintedToken) => void }) {
  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState<string[]>(['tickets:read', 'tickets:write', 'comments:write']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: string) =>
    setScopes((cur) => (cur.includes(id) ? cur.filter((s) => s !== id) : [...cur, id]));

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.createToken(label.trim(), scopes, 90);
      onCreated(result.token);
      setLabel('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not create the token.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">Create an agent token</Typography>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      <TextField
        size="small"
        label="What is it for?"
        placeholder="e.g. james-laptop"
        helperText="Shown in the activity log for anything this token does."
        value={label}
        onChange={(event) => setLabel(event.target.value)}
      />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {SCOPES.map((scope) => (
          <Chip
            key={scope.id}
            label={scope.label}
            color={scopes.includes(scope.id) ? 'primary' : 'default'}
            variant={scopes.includes(scope.id) ? 'filled' : 'outlined'}
            onClick={() => toggle(scope.id)}
            size="small"
          />
        ))}
      </Stack>
      <Box>
        <Button
          variant="contained"
          size="small"
          disabled={busy || !label.trim() || scopes.length === 0}
          onClick={() => void submit()}
        >
          {busy ? 'Creating…' : 'Create token'}
        </Button>
      </Box>
    </Stack>
  );
}

function TokenRow({ token, onRevoke }: { token: TokenSummary; onRevoke: (id: string) => void }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: token.revoked ? 0.5 : 1 }}>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {token.label}
        {token.revoked && ' · revoked'}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        expires {new Date(token.expiresAt).toLocaleDateString()}
      </Typography>
      {!token.revoked && (
        <IconButton size="small" onClick={() => onRevoke(token.id)} aria-label={`Revoke ${token.label}`}>
          <CloseIcon fontSize="inherit" />
        </IconButton>
      )}
    </Stack>
  );
}

export function ConnectAgent({ onClose }: { onClose: () => void }) {
  const [tokens, setTokens] = useState<TokenSummary[] | null>(null);
  const [fresh, setFresh] = useState<MintedToken | null>(null);

  const reload = useCallback(() => {
    api.listTokens().then((r) => setTokens(r.tokens)).catch(() => setTokens([]));
  }, []);
  useEffect(reload, [reload]);

  const revoke = (id: string) => {
    void api.revokeToken(id).then(reload);
  };

  return (
    <Box sx={{ width: { xs: '100vw', sm: 640 }, p: 3 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          Connect an agent
        </Typography>
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Let Claude (or any MCP agent) read and update this board for you. Create a token, run the
        one-line setup, and your agent can work the board directly — everything it does is logged
        under the token's name.
      </Typography>

      {fresh ? (
        <NewTokenReveal token={fresh} onDone={() => { setFresh(null); reload(); }} />
      ) : (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <CreateForm onCreated={setFresh} />
        </Paper>
      )}

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Your tokens
      </Typography>
      {tokens === null ? (
        <CircularProgress size={20} />
      ) : tokens.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          None yet.
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {tokens.map((token) => (
            <TokenRow key={token.id} token={token} onRevoke={revoke} />
          ))}
        </Stack>
      )}
    </Box>
  );
}
